import { useCallback, useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { getCompanyProfile, updateCompanyProfile } from "../../../services";
import { CompanyProfile as CompanyProfileData } from "../../../types/company-profile.type";
import { formatThaiShortDateTimeBkk } from "../../../utils/date";
import CompanyLogoCard from "./CompanyLogoCard";
import CompanyProfileForm from "./CompanyProfileForm";
import {
  CompanyProfileDraft,
  CompanyProfileErrors,
  CompanyProfileField,
  isDraftChanged,
  toDraft,
  toUpdateRequest,
  validateDraft,
} from "./companyProfileDraft";

const LOGO_COLUMN_SIZE = { xs: 12, md: 4 };
const FORM_COLUMN_SIZE = { xs: 12, md: 8 };

/**
 * ข้อมูลบริษัท — the seller printed on every tax invoice, receipt and thermal slip and sent to
 * KTB's eTax. Edits apply to documents issued from now on; issued documents keep theirs.
 */
export default function CompanyProfile() {
  const [profile, setProfile] = useState<CompanyProfileData | null>(null);
  const [draft, setDraft] = useState<CompanyProfileDraft | null>(null);
  const [errors, setErrors] = useState<CompanyProfileErrors>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  const applyProfile = useCallback((stored: CompanyProfileData) => {
    setProfile(stored);
    setDraft(toDraft(stored));
    setErrors({});
  }, []);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    try {
      const { data: body } = await getCompanyProfile();
      if (body.data) applyProfile(body.data);
    } catch {
      // Surfaced by the axios interceptor; the page shows the retry state below.
    } finally {
      setLoading(false);
    }
  }, [applyProfile]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleChangeField = (field: CompanyProfileField, value: string) => {
    setDraft((current) => (current ? { ...current, [field]: value } : current));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  // A new logo changes only the stored profile (logo + last edited); fields being edited in
  // the form stay as they are, unsaved.
  const handleLogoUploaded = (stored: CompanyProfileData) => {
    setProfile(stored);
  };

  const handleRetry = () => {
    loadProfile();
  };

  const handleReset = () => {
    if (profile) applyProfile(profile);
  };

  const handleSave = async () => {
    if (!draft) return;
    const draftErrors = validateDraft(draft);
    setErrors(draftErrors);
    if (Object.keys(draftErrors).length > 0) return;

    setSaving(true);
    try {
      const { data: body } = await updateCompanyProfile(toUpdateRequest(draft));
      if (body.data) applyProfile(body.data);
    } catch {
      // Surfaced by the axios interceptor; keep the draft so the edit can be retried.
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (!profile || !draft) {
    return (
      <Stack alignItems="flex-start" gap={1.5}>
        <Alert severity="error">โหลดข้อมูลบริษัทไม่สำเร็จ</Alert>
        <Button variant="outlined" onClick={handleRetry}>
          ลองอีกครั้ง
        </Button>
      </Stack>
    );
  }

  const isChanged = isDraftChanged(draft, profile);
  const lastUpdated = formatThaiShortDateTimeBkk(profile.updated_at, { weekday: false });
  const lastUpdatedText = profile.updated_by
    ? `แก้ไขล่าสุด ${lastUpdated} โดย ${profile.updated_by}`
    : `แก้ไขล่าสุด ${lastUpdated}`;

  return (
    <Stack gap={2}>
      <Alert severity="info">
        ข้อมูลนี้พิมพ์บนใบกำกับภาษี ใบเสร็จ (รวมใบเสร็จจากเครื่องพิมพ์พกพา) และส่งไป e-Tax ของ KTB
        การแก้ไขมีผลกับเอกสารที่ออกหลังบันทึกเท่านั้น เอกสารที่ออกไปแล้วไม่เปลี่ยน
      </Alert>

      <Grid container spacing={2}>
        <Grid size={LOGO_COLUMN_SIZE}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <CompanyLogoCard logoUrl={profile.logo} onUploaded={handleLogoUploaded} />
          </Paper>
        </Grid>

        <Grid size={FORM_COLUMN_SIZE}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack gap={2}>
              <CompanyProfileForm
                draft={draft}
                errors={errors}
                disabled={saving}
                onChangeField={handleChangeField}
              />

              <Stack gap={0.5}>
                <Typography variant="body2" color="text.secondary">
                  ที่อยู่ที่พิมพ์บนเอกสาร (ตามข้อมูลที่บันทึกแล้ว)
                </Typography>
                <Typography variant="body2">{profile.full_address}</Typography>
              </Stack>

              <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
                <Typography variant="caption" color="text.secondary">
                  {lastUpdatedText}
                </Typography>
                <Stack direction="row" gap={1}>
                  <Button onClick={handleReset} disabled={!isChanged || saving}>
                    ยกเลิก
                  </Button>
                  <Button variant="contained" onClick={handleSave} disabled={!isChanged || saving}>
                    {saving ? "กำลังบันทึก..." : "บันทึก"}
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Stack>
  );
}
